import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Table, Thead, Tbody, Tr, Th, Td, Text, Badge, Button, HStack, VStack,
  Flex, Select, Spinner, useToast, IconButton, Tooltip, Input, Textarea,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  useDisclosure, Card, CardBody, SimpleGrid, Stat, StatLabel, StatNumber, StatHelpText,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton,
  Checkbox, Tag, TagLabel, TagCloseButton, Wrap, WrapItem, Divider, Alert, AlertIcon,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, RepeatIcon, CheckIcon } from '@chakra-ui/icons';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { Send, Zap, Users, Mail, Eye, Sparkles, AlertTriangle, TrendingDown, TrendingUp, Star, ChevronDown, ChevronUp, MousePointerClick, Image as ImageIcon } from 'lucide-react';
import { PABS_FEATURES } from '../../shared/pilotFeatureMetadata';

const EMAIL_TYPES = [
  { value: 'milestone', label: 'Milestone', color: 'green' },
  { value: 're-engagement', label: 'Re-engagement', color: 'orange' },
  { value: 'feature_discovery', label: 'Feature Discovery', color: 'purple' },
  { value: 'positioning_watch', label: 'Positioning Watch', color: 'cyan' },
  { value: 'broadcast', label: 'Broadcast', color: 'blue' },
  { value: 'newsletter', label: 'Newsletter', color: 'teal' },
];

const STATUS_CONFIG = {
  compose: { color: 'teal', label: 'Compose' },
  draft: { color: 'gray', label: 'Draft' },
  approved: { color: 'blue', label: 'Approved' },
  sent: { color: 'green', label: 'Sent' },
  failed: { color: 'red', label: 'Failed' },
};

const typeConfig = (type) => EMAIL_TYPES.find(t => t.value === type) || { label: type, color: 'gray' };

const AdminPilotOutreachTab = ({ participants }) => {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editSubject, setEditSubject] = useState('');
  const [editBody, setEditBody] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showHtml, setShowHtml] = useState(false);
  const editBodyRef = useRef(null); // iframe ref for designMode editing
  const imageFileInputRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null); // tracks clicked image in editor
  const [imageWidth, setImageWidth] = useState('');

  const getEditedHtml = () => {
    if (editBodyRef.current?.contentDocument) {
      // Strip any selection outlines before saving
      editBodyRef.current.contentDocument.querySelectorAll('img').forEach(img => { img.style.outline = ''; });
      return editBodyRef.current.contentDocument.documentElement.outerHTML;
    }
    return editBody;
  };

  const execFormat = (cmd, value) => {
    editBodyRef.current?.contentDocument?.execCommand(cmd, false, value ?? null);
    editBodyRef.current?.contentDocument?.defaultView?.focus();
  };

  const applyEditorBaseStyles = (doc) => {
    if (!doc?.body) return;
    doc.body.style.fontFamily = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,'Helvetica Neue',sans-serif";
    doc.body.style.fontSize = '15px';
    doc.body.style.lineHeight = '1.6';
    doc.body.style.color = '#1A202C';
    doc.body.style.margin = '0';
    doc.body.style.minHeight = '280px';
    doc.body.style.padding = '16px';
    doc.body.style.boxSizing = 'border-box';
  };

  const isLikelyImageUrl = (value) => /^https?:\/\/\S+\.(png|jpe?g|gif|webp|avif|svg)(\?\S*)?$/i.test(String(value || '').trim());

  const escapeAttr = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  const insertImageByUrl = () => {
    const rawUrl = window.prompt('Image URL:');
    if (!rawUrl) return;
    const url = rawUrl.trim();
    if (!/^https?:\/\//i.test(url)) {
      toast({ title: 'Use a full image URL starting with http or https', status: 'warning', duration: 2500 });
      return;
    }
    const alt = window.prompt('Alt text:', '') || '';
    const html = `<p><img src="${escapeAttr(url)}" alt="${escapeAttr(alt)}" style="display:block;max-width:100%;height:auto;border-radius:8px;margin:16px 0;" /></p>`;
    execFormat('insertHTML', html);
  };

  const insertImageHtml = (src, alt = '') => {
    const html = `<p><img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}" style="display:block;max-width:100%;height:auto;border-radius:8px;margin:16px 0;" /></p>`;
    execFormat('insertHTML', html);
  };

  const insertImageFile = (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) {
      toast({ title: 'Please choose an image file', status: 'warning', duration: 2500 });
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'Image is too large for email',
        description: 'Use an image under 2 MB, or host it and insert the image URL.',
        status: 'warning',
        duration: 4000,
      });
      return;
    }
    const reader = new FileReader();
    reader.onload = () => insertImageHtml(reader.result, file.name?.replace(/\.[^.]+$/, '') || '');
    reader.onerror = () => toast({ title: 'Could not read image', status: 'error', duration: 2500 });
    reader.readAsDataURL(file);
  };

  const insertImageFiles = (files) => {
    Array.from(files || []).filter(file => file.type?.startsWith('image/')).forEach(insertImageFile);
  };

  const applyImageWidth = (widthValue) => {
    if (!selectedImage) return;
    selectedImage.style.width = widthValue;
    selectedImage.style.maxWidth = '100%';
    setImageWidth(widthValue);
  };

  const clearImageSelection = () => {
    if (selectedImage) {
      selectedImage.style.outline = '';
    }
    setSelectedImage(null);
    setImageWidth('');
  };

  const [genParticipant, setGenParticipant] = useState('');
  const [genType, setGenType] = useState('milestone');
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [broadcastMessage, setBroadcastMessage] = useState('');
  const [recommendations, setRecommendations] = useState([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [recsExpanded, setRecsExpanded] = useState(true);
  const [recsGeneratedAt, setRecsGeneratedAt] = useState(null);

  const { isOpen: isGenOpen, onOpen: onGenOpen, onClose: onGenClose } = useDisclosure();
  const { isOpen: isBroadcastOpen, onOpen: onBroadcastOpen, onClose: onBroadcastClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();

  const toast = useToast();

  const fetchEmails = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterType) params.set('type', filterType);
      const res = await fetchWithAuth(`/api/admin/pilot/emails?${params.toString()}`);
      const data = await res.json();
      setEmails(data);
    } catch (err) {
      console.error('Error fetching emails:', err);
    }
    setLoading(false);
  }, [filterStatus, filterType]);

  useEffect(() => { fetchEmails(); }, [fetchEmails]);

  const fetchRecommendations = async () => {
    setRecsLoading(true);
    try {
      const res = await fetchWithAuth('/api/admin/pilot/emails/recommendations');
      const data = await res.json();
      if (res.ok) {
        setRecommendations(data.recommendations || []);
        setRecsGeneratedAt(data.generated_at);
      } else {
        toast({ title: data.error || 'Failed to get recommendations', status: 'error', duration: 3000 });
      }
    } catch (err) {
      toast({ title: 'Error getting recommendations', status: 'error', duration: 3000 });
    }
    setRecsLoading(false);
  };

  const handleRecAction = (rec) => {
    const participant = (participants || []).find(p => p.email === rec.email);
    if (!participant) return;
    setGenParticipant(String(participant.id));
    setGenType(rec.recommendedType === 'skip' ? 'milestone' : rec.recommendedType);
    if (rec.recommendedType === 'feature_discovery' && rec.suggestedFeatures?.length > 0) {
      const featureLabels = rec.suggestedFeatures.map(f => {
        const exact = PABS_FEATURES.find(pf => pf.label.toLowerCase() === f.toLowerCase());
        if (exact) return exact.label;
        const partial = PABS_FEATURES.find(pf => pf.label.toLowerCase().includes(f.toLowerCase()) || f.toLowerCase().includes(pf.label.toLowerCase()));
        if (partial) return partial.label;
        const idMatch = PABS_FEATURES.find(pf => pf.id.toLowerCase().replace(/_/g, ' ').includes(f.toLowerCase().replace(/_/g, ' ')));
        return idMatch ? idMatch.label : null;
      }).filter(Boolean);
      setSelectedFeatures([...new Set(featureLabels)]);
    } else {
      setSelectedFeatures([]);
    }
    onGenOpen();
  };

  const toggleFeature = (featureLabel) => {
    setSelectedFeatures(prev =>
      prev.includes(featureLabel)
        ? prev.filter(f => f !== featureLabel)
        : [...prev, featureLabel]
    );
  };

  const generateEmail = async () => {
    if (!genParticipant || !genType) return;
    setGenerating(true);
    try {
      const payload = { participant_id: parseInt(genParticipant), email_type: genType };
      if (genType === 'feature_discovery' && selectedFeatures.length > 0) {
        payload.selected_features = selectedFeatures;
      }
      const res = await fetchWithAuth('/api/admin/pilot/emails/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        toast({ title: 'Draft generated', status: 'success', duration: 3000 });
        onGenClose();
        setSelectedFeatures([]);
        fetchEmails();
      } else {
        toast({ title: data.error || 'Failed to generate', status: 'error', duration: 3000 });
      }
    } catch (err) {
      toast({ title: 'Error generating email', status: 'error', duration: 3000 });
    }
    setGenerating(false);
  };

  const generateBroadcast = async () => {
    if (!broadcastMessage.trim()) return;
    setGenerating(true);
    try {
      const res = await fetchWithAuth('/api/admin/pilot/emails/broadcast', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: broadcastMessage, email_type: 'broadcast' }),
      });
      const data = await res.json();
      if (res.ok) {
        toast({ title: `${data.drafts_created} drafts created`, status: 'success', duration: 3000 });
        onBroadcastClose();
        setBroadcastMessage('');
        fetchEmails();
      } else {
        toast({ title: data.error || 'Failed', status: 'error', duration: 3000 });
      }
    } catch (err) {
      toast({ title: 'Error creating broadcast', status: 'error', duration: 3000 });
    }
    setGenerating(false);
  };

  const createNewsletter = async () => {
    const newsletter = {
      id: 'newsletter-compose',
      email_type: 'newsletter',
      status: 'compose',
      subject: 'PABS Newsletter',
      body: '',
      recipient_name: 'All active users',
      recipient_email: 'bcc:all-active-users',
      created_at: null,
    };
    setSelectedEmail(newsletter);
    setEditSubject(newsletter.subject);
    setEditBody(newsletter.body);
    setIsEditing(true);
    setShowHtml(false);
    onDetailOpen();
  };

  const saveNewsletterDraft = async () => {
    const body = showHtml ? editBody : getEditedHtml();
    if (!editSubject.trim()) {
      toast({ title: 'Subject is required', status: 'error', duration: 2500 });
      return;
    }
    setSendingId('newsletter-compose');
    try {
      const res = await fetchWithAuth('/api/admin/pilot/emails/newsletter/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: editSubject, body }),
      });
      const data = await res.json();
      if (res.ok) {
        toast({ title: 'Newsletter draft saved', status: 'success', duration: 2500 });
        setSelectedEmail(data);
        setEditSubject(data.subject);
        setEditBody(data.body);
        setIsEditing(false);
        fetchEmails();
      } else {
        toast({ title: data.error || 'Failed to save newsletter draft', description: data.details, status: 'error', duration: 4000 });
      }
    } catch (err) {
      toast({ title: 'Error saving newsletter draft', status: 'error', duration: 3000 });
    }
    setSendingId(null);
  };

  const approveEmail = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/emails/${id}/approve`, { method: 'POST' });
      if (res.ok) {
        toast({ title: 'Email approved', status: 'success', duration: 2000 });
        fetchEmails();
        if (selectedEmail?.id === id) {
          const data = await res.json();
          setSelectedEmail(data);
        }
      }
    } catch (err) {
      toast({ title: 'Error approving', status: 'error', duration: 2000 });
    }
  };

  const sendEmail = async (id) => {
    setSendingId(id);
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/emails/${id}/send`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast({ title: 'Email sent!', status: 'success', duration: 3000 });
        fetchEmails();
        if (selectedEmail?.id === id) setSelectedEmail(prev => ({ ...prev, status: 'sent' }));
      } else {
        toast({ title: data.error || 'Send failed', status: 'error', duration: 4000 });
      }
    } catch (err) {
      toast({ title: 'Error sending email', status: 'error', duration: 3000 });
    }
    setSendingId(null);
  };

  const regenerateEmail = async (id) => {
    setGenerating(true);
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/emails/${id}/regenerate`, { method: 'POST' });
      const data = await res.json();
      if (res.ok) {
        toast({ title: 'Email regenerated', status: 'success', duration: 2000 });
        setSelectedEmail(data);
        fetchEmails();
      }
    } catch (err) {
      toast({ title: 'Error regenerating', status: 'error', duration: 2000 });
    }
    setGenerating(false);
  };

  const deleteEmail = async (id) => {
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/emails/${id}`, { method: 'DELETE' });
      if (res.ok) {
        toast({ title: 'Email deleted', status: 'success', duration: 2000 });
        if (selectedEmail?.id === id) { setSelectedEmail(null); onDetailClose(); }
        fetchEmails();
      }
    } catch (err) {
      toast({ title: 'Error deleting', status: 'error', duration: 2000 });
    }
  };

  const saveEdit = async () => {
    if (!selectedEmail) return;
    try {
      const res = await fetchWithAuth(`/api/admin/pilot/emails/${selectedEmail.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: editSubject, body: showHtml ? editBody : getEditedHtml() }),
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedEmail(data);
        setEditBody(data.body);
        setIsEditing(false);
        setShowHtml(false);
        toast({ title: 'Saved', status: 'success', duration: 2000 });
        fetchEmails();
      }
    } catch (err) {
      toast({ title: 'Error saving', status: 'error', duration: 2000 });
    }
  };

  const batchApprove = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await fetchWithAuth('/api/admin/pilot/emails/batch-approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json();
      if (res.ok) {
        toast({ title: `${data.approved} emails approved`, status: 'success', duration: 2000 });
        setSelectedIds([]);
        fetchEmails();
      }
    } catch (err) {
      toast({ title: 'Error batch approving', status: 'error', duration: 2000 });
    }
  };

  const batchSend = async () => {
    if (selectedIds.length === 0) return;
    setSendingId('batch');
    try {
      const res = await fetchWithAuth('/api/admin/pilot/emails/batch-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: selectedIds }),
      });
      const data = await res.json();
      if (res.ok) {
        const sent = data.results.filter(r => r.status === 'sent').length;
        toast({ title: `${sent} emails sent`, status: 'success', duration: 3000 });
        setSelectedIds([]);
        fetchEmails();
      }
    } catch (err) {
      toast({ title: 'Error batch sending', status: 'error', duration: 3000 });
    }
    setSendingId(null);
  };

  const openDetail = (email) => {
    setSelectedEmail(email);
    setEditSubject(email.subject);
    setEditBody(email.body);
    setIsEditing(false);
    onDetailOpen();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const draftIds = emails.filter(e => e.status === 'draft' || e.status === 'approved').map(e => e.id);
    if (selectedIds.length === draftIds.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(draftIds);
    }
  };

  const stats = {
    total: emails.length,
    drafts: emails.filter(e => e.status === 'draft').length,
    approved: emails.filter(e => e.status === 'approved').length,
    sent: emails.filter(e => e.status === 'sent').length,
    failed: emails.filter(e => e.status === 'failed').length,
  };

  const formatDate = (d) => {
    if (!d) return '--';
    return new Date(d).toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Box>
      <SimpleGrid columns={{ base: 2, md: 5 }} spacing={3} mb={4}>
        <Card variant="outline" size="sm">
          <CardBody py={2}>
            <Stat size="sm">
              <StatLabel fontSize="xs">Total Emails</StatLabel>
              <StatNumber fontSize="lg">{stats.total}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline" size="sm">
          <CardBody py={2}>
            <Stat size="sm">
              <StatLabel fontSize="xs">Drafts</StatLabel>
              <StatNumber fontSize="lg" color="gray.500">{stats.drafts}</StatNumber>
              <StatHelpText fontSize="xs" mb={0}>awaiting review</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline" size="sm">
          <CardBody py={2}>
            <Stat size="sm">
              <StatLabel fontSize="xs">Approved</StatLabel>
              <StatNumber fontSize="lg" color="blue.500">{stats.approved}</StatNumber>
              <StatHelpText fontSize="xs" mb={0}>ready to send</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline" size="sm">
          <CardBody py={2}>
            <Stat size="sm">
              <StatLabel fontSize="xs">Sent</StatLabel>
              <StatNumber fontSize="lg" color="green.500">{stats.sent}</StatNumber>
              <StatHelpText fontSize="xs" mb={0}>delivered</StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        <Card variant="outline" size="sm">
          <CardBody py={2}>
            <Stat size="sm">
              <StatLabel fontSize="xs">Failed</StatLabel>
              <StatNumber fontSize="lg" color="red.500">{stats.failed}</StatNumber>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      <Card variant="outline" mb={4} borderColor="gray.300" bg="gray.50">
        <CardBody py={3}>
          <Flex justify="space-between" align="center" cursor="pointer" onClick={() => setRecsExpanded(!recsExpanded)}>
            <HStack spacing={2}>
              <Sparkles size={16} color="#2d3748" />
              <Text fontSize="sm" fontWeight="bold" color="gray.800">AI Outreach Strategy</Text>
              {recsGeneratedAt && (
                <Text fontSize="xs" color="gray.500">
                  Updated {new Date(recsGeneratedAt).toLocaleString('en-NZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </Text>
              )}
            </HStack>
            <HStack spacing={2}>
              {recommendations.length === 0 && !recsLoading && (
                <Button size="xs" bg="gray.700" color="white" _hover={{ bg: 'gray.600' }} onClick={(e) => { e.stopPropagation(); fetchRecommendations(); }} leftIcon={<Sparkles size={12} />}>
                  Analyse Participants
                </Button>
              )}
              {recommendations.length > 0 && (
                <Button size="xs" variant="outline" borderColor="gray.500" color="gray.700" _hover={{ bg: 'gray.100' }} onClick={(e) => { e.stopPropagation(); fetchRecommendations(); }} leftIcon={<Sparkles size={12} />} isLoading={recsLoading}>
                  Refresh
                </Button>
              )}
              {recsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </HStack>
          </Flex>

          {recsLoading && (
            <Flex justify="center" py={6}>
              <VStack spacing={2}>
                <Spinner size="md" color="gray.600" />
                <Text fontSize="xs" color="gray.600">Analysing participant data and outreach history...</Text>
              </VStack>
            </Flex>
          )}

          {recsExpanded && !recsLoading && recommendations.length > 0 && (
            <Box mt={3}>
              <VStack spacing={2} align="stretch">
                {recommendations.filter(r => r.recommendedType !== 'skip').map((rec, idx) => {
                  const riskConfig = {
                    churning: { color: 'red', icon: AlertTriangle, label: 'Churning' },
                    cooling: { color: 'orange', icon: TrendingDown, label: 'Cooling' },
                    engaged: { color: 'green', icon: TrendingUp, label: 'Engaged' },
                    champion: { color: 'blue', icon: Star, label: 'Champion' },
                  };
                  const risk = riskConfig[rec.risk] || riskConfig.cooling;
                  const RiskIcon = risk.icon;
                  const priorityColors = { high: 'red', medium: 'orange', low: 'gray' };

                  return (
                    <Card key={idx} variant="outline" size="sm" bg="white">
                      <CardBody py={2} px={3}>
                        <Flex justify="space-between" align="start">
                          <Box flex="1" mr={3}>
                            <HStack spacing={2} mb={1}>
                              <Text fontSize="sm" fontWeight="bold">{rec.name}</Text>
                              <Badge colorScheme={priorityColors[rec.priority] || 'gray'} variant="solid" fontSize="10px">
                                {rec.priority}
                              </Badge>
                              <Badge colorScheme={risk.color} variant="subtle" fontSize="10px">
                                <HStack spacing={1}>
                                  <RiskIcon size={10} />
                                  <Text>{risk.label}</Text>
                                </HStack>
                              </Badge>
                              <Badge colorScheme={typeConfig(rec.recommendedType || 'milestone').color} variant="outline" fontSize="10px">
                                {typeConfig(rec.recommendedType || 'milestone').label}
                              </Badge>
                            </HStack>
                            <Text fontSize="xs" color="gray.600" mb={1}>{rec.reason}</Text>
                            <Text fontSize="xs" color="gray.500" fontStyle="italic">{rec.focus}</Text>
                            {rec.suggestedFeatures?.length > 0 && (
                              <Wrap spacing={1} mt={1}>
                                {rec.suggestedFeatures.map((f, i) => (
                                  <WrapItem key={i}>
                                    <Tag size="sm" bg="gray.100" color="gray.700" fontSize="10px">
                                      <TagLabel>{f}</TagLabel>
                                    </Tag>
                                  </WrapItem>
                                ))}
                              </Wrap>
                            )}
                          </Box>
                          <Button
                            size="xs"
                            variant="outline"
                            borderColor="gray.400"
                            color="gray.700"
                            _hover={{ bg: 'gray.100' }}
                            onClick={() => handleRecAction(rec)}
                            leftIcon={<Zap size={12} />}
                            flexShrink={0}
                          >
                            Generate
                          </Button>
                        </Flex>
                      </CardBody>
                    </Card>
                  );
                })}
                {recommendations.filter(r => r.recommendedType === 'skip').length > 0 && (
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    {recommendations.filter(r => r.recommendedType === 'skip').length} participant(s) don't need outreach right now:{' '}
                    {recommendations.filter(r => r.recommendedType === 'skip').map(r => r.name).join(', ')}
                  </Text>
                )}
              </VStack>
            </Box>
          )}

          {recsExpanded && !recsLoading && recommendations.length === 0 && (
            <Text fontSize="xs" color="gray.500" mt={2}>
              Click "Analyse Participants" to get AI-powered suggestions on who to reach out to first and why.
            </Text>
          )}
        </CardBody>
      </Card>

      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <HStack spacing={2}>
          <Select size="sm" w="130px" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} placeholder="All statuses">
            <option value="draft">Drafts</option>
            <option value="approved">Approved</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
          </Select>
          <Select size="sm" w="160px" value={filterType} onChange={(e) => setFilterType(e.target.value)} placeholder="All types">
            {EMAIL_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          {selectedIds.length > 0 && (
            <>
              <Text fontSize="xs" color="gray.500">{selectedIds.length} selected</Text>
              <Button size="sm" colorScheme="blue" variant="outline" onClick={batchApprove} leftIcon={<CheckIcon />}>
                Approve All
              </Button>
              <Button size="sm" colorScheme="green" variant="outline" onClick={batchSend} isLoading={sendingId === 'batch'}>
                Send All
              </Button>
            </>
          )}
          <Button size="sm" colorScheme="blue" onClick={onGenOpen} leftIcon={<Zap size={14} />}>
            Generate Email
          </Button>
          <Button size="sm" colorScheme="purple" onClick={onBroadcastOpen} leftIcon={<Users size={14} />}>
            New Broadcast
          </Button>
          <Button size="sm" colorScheme="teal" variant="outline" onClick={createNewsletter} leftIcon={<Mail size={14} />}>
            Newsletter
          </Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={8}><Spinner /></Flex>
      ) : emails.length === 0 ? (
        <Box textAlign="center" py={8} color="gray.500">
          <Mail size={32} style={{ margin: '0 auto 8px' }} />
          <Text fontSize="sm">No emails yet. Generate your first email, create a broadcast, or draft a newsletter.</Text>
        </Box>
      ) : (
        <Box bg="white" borderRadius="md" overflow="auto" boxShadow="sm" border="1px solid" borderColor="gray.200">
          <Table size="sm" variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th w="30px">
                  <Checkbox
                    size="sm"
                    isChecked={selectedIds.length > 0 && selectedIds.length === emails.filter(e => e.status === 'draft' || e.status === 'approved').length}
                    onChange={toggleSelectAll}
                  />
                </Th>
                <Th fontSize="xs" minW="140px">Recipient</Th>
                <Th fontSize="xs">Type</Th>
                <Th fontSize="xs" minW="200px">Subject</Th>
                <Th fontSize="xs" textAlign="center">Status</Th>
                <Th fontSize="xs">Created</Th>
                <Th fontSize="xs">Sent</Th>
                <Th fontSize="xs" textAlign="center">Opened</Th>
                <Th fontSize="xs" textAlign="center">Clicked</Th>
                <Th fontSize="xs" w="100px">Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {emails.map(email => {
                const sc = STATUS_CONFIG[email.status] || STATUS_CONFIG.draft;
                const tc = typeConfig(email.email_type);
                return (
                  <Tr key={email.id} _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => openDetail(email)}>
                    <Td onClick={(e) => e.stopPropagation()}>
                      {(email.status === 'draft' || email.status === 'approved') && (
                        <Checkbox size="sm" isChecked={selectedIds.includes(email.id)} onChange={() => toggleSelect(email.id)} />
                      )}
                    </Td>
                    <Td>
                      <HStack spacing={1} align="center">
                        <Text fontSize="xs" fontWeight="medium">{email.recipient_name || email.first_name}</Text>
                        {email.email_unsubscribed && (
                          <Badge colorScheme="red" variant="subtle" fontSize="9px">unsub</Badge>
                        )}
                      </HStack>
                      <Text fontSize="xs" color="gray.400">
                        {email.email_type === 'newsletter' ? 'Personalized newsletter' : email.recipient_email}
                      </Text>
                    </Td>
                    <Td>
                      <Badge colorScheme={tc.color} variant="subtle" fontSize="xs">{tc.label}</Badge>
                    </Td>
                    <Td>
                      <Text fontSize="xs" noOfLines={1}>{email.subject}</Text>
                    </Td>
                    <Td textAlign="center">
                      <Badge colorScheme={sc.color} variant="solid" fontSize="xs">{sc.label}</Badge>
                    </Td>
                    <Td><Text fontSize="xs" color="gray.500">{formatDate(email.created_at)}</Text></Td>
                    <Td><Text fontSize="xs" color="gray.500">{formatDate(email.sent_at)}</Text></Td>
                    <Td textAlign="center">
                      {email.status === 'sent' && email.opened_at ? (
                        <Tooltip label={`Opened ${formatDate(email.opened_at)}${email.open_count > 1 ? ` (${email.open_count}x)` : ''}`} fontSize="xs">
                          <Badge colorScheme="green" variant="subtle" fontSize="10px" cursor="default">
                            <HStack spacing={1}><Eye size={10} /><Text>{email.open_count || 1}x</Text></HStack>
                          </Badge>
                        </Tooltip>
                      ) : email.status === 'sent' ? (
                        <Text fontSize="xs" color="gray.400">--</Text>
                      ) : null}
                    </Td>
                    <Td textAlign="center">
                      {email.status === 'sent' && email.clicked_at ? (
                        <Tooltip label={`Clicked ${formatDate(email.clicked_at)}${email.click_count > 1 ? ` (${email.click_count}x)` : ''}`} fontSize="xs">
                          <Badge colorScheme="blue" variant="subtle" fontSize="10px" cursor="default">
                            <HStack spacing={1}><MousePointerClick size={10} /><Text>{email.click_count || 1}x</Text></HStack>
                          </Badge>
                        </Tooltip>
                      ) : email.status === 'sent' ? (
                        <Text fontSize="xs" color="gray.400">--</Text>
                      ) : null}
                    </Td>
                    <Td onClick={(e) => e.stopPropagation()}>
                      <HStack spacing={1}>
                        {email.status === 'draft' && (
                          <>
                            <Tooltip label="Approve" fontSize="xs">
                              <IconButton icon={<CheckIcon />} size="xs" colorScheme="blue" variant="ghost" onClick={() => approveEmail(email.id)} />
                            </Tooltip>
                            <Tooltip label="Delete" fontSize="xs">
                              <IconButton icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => deleteEmail(email.id)} />
                            </Tooltip>
                          </>
                        )}
                        {email.status === 'approved' && (
                          <>
                            <Tooltip label="Send" fontSize="xs">
                              <IconButton icon={<Send size={12} />} size="xs" colorScheme="green" variant="ghost" onClick={() => sendEmail(email.id)} isLoading={sendingId === email.id} />
                            </Tooltip>
                            <Tooltip label="Delete" fontSize="xs">
                              <IconButton icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => deleteEmail(email.id)} />
                            </Tooltip>
                          </>
                        )}
                        {email.status === 'failed' && (
                          <Tooltip label="Delete" fontSize="xs">
                            <IconButton icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => deleteEmail(email.id)} />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isGenOpen} onClose={onGenClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">Generate Email</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="xs" color="gray.500" mb={3}>
              AI will generate a personalized email based on the participant's usage data, engagement signals, and pilot week.
            </Text>
            <VStack spacing={3}>
              <Box w="100%">
                <Text fontSize="xs" fontWeight="medium" mb={1}>Participant</Text>
                <Select size="sm" value={genParticipant} onChange={(e) => setGenParticipant(e.target.value)} placeholder="Select participant">
                  {(participants || []).map(p => (
                    <option key={p.id} value={p.id}>
                      {`${p.first_name || ''} ${p.last_name || ''}`.trim() || p.email} ({p.auto_verdict}){p.email_unsubscribed ? ' — unsubscribed' : ''}
                    </option>
                  ))}
                </Select>
              </Box>
              <Box w="100%">
                <Text fontSize="xs" fontWeight="medium" mb={1}>Email Type</Text>
                <Select size="sm" value={genType} onChange={(e) => { setGenType(e.target.value); setSelectedFeatures([]); }}>
                  {EMAIL_TYPES.filter(t => !['broadcast', 'newsletter'].includes(t.value)).map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.400" mt={1}>
                  {genType === 'milestone' && 'Celebrates progress and encourages next steps.'}
                  {genType === 're-engagement' && 'Gently re-engages inactive users.'}
                  {genType === 'feature_discovery' && 'Highlights features they haven\'t tried yet.'}
                  {genType === 'positioning_watch' && 'Checks live market/competitor signals against their specific project, with cited sources.'}
                </Text>
              </Box>
              {genType === 'feature_discovery' && (
                <Box w="100%">
                  <Text fontSize="xs" fontWeight="medium" mb={2}>Features to Highlight</Text>
                  <Text fontSize="xs" color="gray.400" mb={2}>Select which features to introduce. AI will explain how each connects to the participant's plan and goals.</Text>
                  <Box maxH="200px" overflowY="auto" border="1px solid" borderColor="gray.200" borderRadius="md" p={2}>
                    <VStack spacing={1} align="stretch">
                      {PABS_FEATURES.map(f => (
                        <Box
                          key={f.id}
                          p={2}
                          borderRadius="md"
                          bg={selectedFeatures.includes(f.label) ? 'gray.100' : 'transparent'}
                          cursor="pointer"
                          onClick={() => toggleFeature(f.label)}
                          _hover={{ bg: selectedFeatures.includes(f.label) ? 'gray.200' : 'gray.50' }}
                        >
                          <HStack spacing={2}>
                            <Checkbox
                              size="sm"
                              isChecked={selectedFeatures.includes(f.label)}
                              onChange={() => toggleFeature(f.label)}
                              colorScheme="gray"
                            />
                            <Box>
                              <Text fontSize="xs" fontWeight="medium">{f.label}</Text>
                              <Text fontSize="xs" color="gray.500" lineHeight="1.3">{f.description}</Text>
                            </Box>
                          </HStack>
                        </Box>
                      ))}
                    </VStack>
                  </Box>
                  {selectedFeatures.length > 0 && (
                    <Wrap spacing={1} mt={2}>
                      {selectedFeatures.map(f => (
                        <WrapItem key={f}>
                          <Tag size="sm" colorScheme="gray" variant="subtle" cursor="pointer" onClick={() => toggleFeature(f)}>
                            <TagLabel>{f}</TagLabel>
                            <TagCloseButton />
                          </Tag>
                        </WrapItem>
                      ))}
                    </Wrap>
                  )}
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" mr={2} onClick={onGenClose}>Cancel</Button>
            <Button size="sm" colorScheme="blue" onClick={generateEmail} isLoading={generating} isDisabled={!genParticipant}>
              Generate Draft
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isBroadcastOpen} onClose={onBroadcastClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader fontSize="md">New Broadcast</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="xs" color="gray.500" mb={3}>
              Describe the message you want to send. AI will create personalized versions for each active participant based on their usage and context.
            </Text>
            <Textarea
              size="sm"
              value={broadcastMessage}
              onChange={(e) => setBroadcastMessage(e.target.value)}
              placeholder="e.g., We just launched competitor analysis! Try it with your latest plan..."
              rows={4}
            />
            <Text fontSize="xs" color="gray.400" mt={2}>
              This will create {(participants || []).filter(p => p.status === 'Active').length} personalized drafts for review before sending.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button size="sm" mr={2} onClick={onBroadcastClose}>Cancel</Button>
            <Button size="sm" colorScheme="purple" onClick={generateBroadcast} isLoading={generating} isDisabled={!broadcastMessage.trim()}>
              Generate Drafts
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Drawer isOpen={isDetailOpen} onClose={onDetailClose} size="lg" placement="right">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader fontSize="md" pb={2}>
            <VStack align="start" spacing={1}>
              <HStack>
                <Text>Email Detail</Text>
                {selectedEmail && (
                  <Badge colorScheme={STATUS_CONFIG[selectedEmail.status]?.color} variant="solid" fontSize="xs">
                    {STATUS_CONFIG[selectedEmail.status]?.label}
                  </Badge>
                )}
              </HStack>
              {selectedEmail && (
                <Text fontSize="xs" color="gray.500" fontWeight="normal">
                  {selectedEmail.email_type === 'newsletter'
                    ? `To: ${selectedEmail.recipient_name || 'All active users'}`
                    : `To: ${selectedEmail.recipient_name} (${selectedEmail.recipient_email})`}
                </Text>
              )}
            </VStack>
          </DrawerHeader>
          <DrawerBody>
            {selectedEmail && (
              <VStack spacing={4} align="stretch">
                <HStack spacing={2}>
                  <Badge colorScheme={typeConfig(selectedEmail.email_type).color} variant="subtle" fontSize="xs">
                    {typeConfig(selectedEmail.email_type).label}
                  </Badge>
                  <Text fontSize="xs" color="gray.400">Created {formatDate(selectedEmail.created_at)}</Text>
                  {selectedEmail.sent_at && <Text fontSize="xs" color="green.500">Sent {formatDate(selectedEmail.sent_at)}</Text>}
                  {selectedEmail.opened_at && (
                    <Badge colorScheme="green" variant="subtle" fontSize="xs">
                      <HStack spacing={1}><Eye size={10} /><Text>Opened {formatDate(selectedEmail.opened_at)}{selectedEmail.open_count > 1 ? ` (${selectedEmail.open_count}x)` : ''}</Text></HStack>
                    </Badge>
                  )}
                  {selectedEmail.clicked_at && (
                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                      <HStack spacing={1}><MousePointerClick size={10} /><Text>Clicked {formatDate(selectedEmail.clicked_at)}{selectedEmail.click_count > 1 ? ` (${selectedEmail.click_count}x)` : ''}</Text></HStack>
                    </Badge>
                  )}
                </HStack>

                {selectedEmail.error_message && (
                  <Alert status="error" size="sm" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="xs">{selectedEmail.error_message}</Text>
                  </Alert>
                )}

                <Divider />

                {isEditing ? (
                  <>
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" mb={1}>Subject</Text>
                      <Input size="sm" value={editSubject} onChange={(e) => setEditSubject(e.target.value)} />
                    </Box>
                    <Box>
                      <Flex justify="space-between" align="center" mb={1}>
                        <Box>
                          <Text fontSize="xs" fontWeight="medium">Body</Text>
                          {selectedEmail.email_type === 'newsletter' && (
                            <Text fontSize="xs" color="gray.500">
                              Merge tags: [Name], [First name], [Last name], [Email]
                            </Text>
                          )}
                        </Box>
                        <Button
                          size="xs"
                          variant="ghost"
                          colorScheme="gray"
                          onClick={() => {
                            if (!showHtml) {
                              setEditBody(getEditedHtml());
                            }
                            clearImageSelection();
                            setShowHtml(v => !v);
                          }}
                        >
                          {showHtml ? 'Visual' : 'HTML'}
                        </Button>
                      </Flex>
                      {showHtml ? (
                        <Textarea
                          size="sm"
                          value={editBody}
                          onChange={(e) => setEditBody(e.target.value)}
                          rows={14}
                          fontFamily="mono"
                          fontSize="xs"
                        />
                      ) : (
                        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                          <HStack
                            spacing={0}
                            borderBottom="1px solid"
                            borderColor="gray.200"
                            bg="gray.50"
                            px={2}
                            py={1}
                            flexWrap="wrap"
                          >
                            {[
                              { cmd: 'bold', label: 'B', style: { fontWeight: 'bold' } },
                              { cmd: 'italic', label: 'I', style: { fontStyle: 'italic' } },
                              { cmd: 'underline', label: 'U', style: { textDecoration: 'underline' } },
                            ].map(({ cmd, label, style }) => (
                              <Tooltip key={cmd} label={cmd.charAt(0).toUpperCase() + cmd.slice(1)} fontSize="xs">
                                <Button size="xs" variant="ghost" onMouseDown={(e) => { e.preventDefault(); execFormat(cmd); }} style={style} minW="28px">
                                  {label}
                                </Button>
                              </Tooltip>
                            ))}
                            <Box w="1px" h="16px" bg="gray.300" mx={1} />
                            {[
                              { cmd: 'insertUnorderedList', label: '• List' },
                              { cmd: 'insertOrderedList', label: '1. List' },
                            ].map(({ cmd, label }) => (
                              <Tooltip key={cmd} label={label} fontSize="xs">
                                <Button size="xs" variant="ghost" onMouseDown={(e) => { e.preventDefault(); execFormat(cmd); }} minW="44px" fontSize="xs">
                                  {label}
                                </Button>
                              </Tooltip>
                            ))}
                            <Box w="1px" h="16px" bg="gray.300" mx={1} />
                            <Tooltip label="Link" fontSize="xs">
                              <Button size="xs" variant="ghost" fontSize="xs" onMouseDown={(e) => { e.preventDefault(); const url = window.prompt('URL:'); if (url) execFormat('createLink', url); }}>
                                Link
                              </Button>
                            </Tooltip>
                            <Tooltip label="Remove link" fontSize="xs">
                              <Button size="xs" variant="ghost" fontSize="xs" onMouseDown={(e) => { e.preventDefault(); execFormat('unlink'); }}>
                                Unlink
                              </Button>
                            </Tooltip>
                            <Tooltip label="Insert image by URL" fontSize="xs">
                              <IconButton
                                aria-label="Insert image by URL"
                                icon={<ImageIcon size={14} />}
                                size="xs"
                                variant="ghost"
                                onMouseDown={(e) => { e.preventDefault(); insertImageByUrl(); }}
                              />
                            </Tooltip>
                            <Tooltip label="Upload image" fontSize="xs">
                              <Button
                                size="xs"
                                variant="ghost"
                                fontSize="xs"
                                leftIcon={<ImageIcon size={13} />}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  imageFileInputRef.current?.click();
                                }}
                              >
                                Upload
                              </Button>
                            </Tooltip>
                            <Input
                              ref={imageFileInputRef}
                              type="file"
                              accept="image/*"
                              multiple
                              display="none"
                              onChange={(e) => {
                                insertImageFiles(e.target.files);
                                e.target.value = '';
                              }}
                            />
                            <Box w="1px" h="16px" bg="gray.300" mx={1} />
                            <Tooltip label="Clear formatting" fontSize="xs">
                              <Button size="xs" variant="ghost" fontSize="xs" onMouseDown={(e) => { e.preventDefault(); execFormat('removeFormat'); }}>
                                Clear
                              </Button>
                            </Tooltip>
                          </HStack>
                          {selectedImage && (
                            <HStack
                              spacing={1}
                              borderBottom="1px solid"
                              borderColor="blue.200"
                              bg="blue.50"
                              px={2}
                              py={1}
                            >
                              <Text fontSize="xs" fontWeight="medium" color="blue.700" mr={1}>Image size:</Text>
                              {['25%', '50%', '75%', '100%'].map(w => (
                                <Button
                                  key={w}
                                  size="xs"
                                  variant={imageWidth === w ? 'solid' : 'outline'}
                                  colorScheme="blue"
                                  onMouseDown={(e) => { e.preventDefault(); applyImageWidth(w); }}
                                  minW="36px"
                                  fontSize="xs"
                                >
                                  {w}
                                </Button>
                              ))}
                              <Box w="1px" h="16px" bg="blue.200" mx={1} />
                              <Input
                                size="xs"
                                w="70px"
                                placeholder="e.g. 300px"
                                value={imageWidth.includes('%') ? '' : imageWidth}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  setImageWidth(val);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    let val = imageWidth.trim();
                                    if (val && !val.endsWith('px') && !val.endsWith('%')) val += 'px';
                                    applyImageWidth(val);
                                  }
                                }}
                                onBlur={() => {
                                  let val = imageWidth.trim();
                                  if (val && !val.endsWith('px') && !val.endsWith('%')) val += 'px';
                                  if (val) applyImageWidth(val);
                                }}
                                fontSize="xs"
                                borderColor="blue.300"
                              />
                              <Tooltip label="Remove image" fontSize="xs">
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectedImage.parentElement?.removeChild(selectedImage);
                                    clearImageSelection();
                                  }}
                                  aria-label="Remove image"
                                />
                              </Tooltip>
                            </HStack>
                          )}
                          <iframe
                            ref={editBodyRef}
                            title="Email Editor"
                            srcDoc={editBody}
                            style={{ width: '100%', minHeight: '300px', maxHeight: '60vh', border: 'none', display: 'block' }}
                            onLoad={() => {
                              const doc = editBodyRef.current?.contentDocument;
                              if (!doc) return;
                              applyEditorBaseStyles(doc);
                              if (doc.body && !doc.body.textContent.trim() && !doc.body.querySelector('img,table,hr,a,button')) {
                                doc.body.innerHTML = '<p><br></p>';
                                applyEditorBaseStyles(doc);
                              }
                              doc.designMode = 'on';
                              doc.addEventListener('click', (e) => {
                                if (e.target.tagName === 'IMG') {
                                  e.preventDefault();
                                  // Clear previous selection outline
                                  doc.querySelectorAll('img').forEach(img => { img.style.outline = ''; });
                                  e.target.style.outline = '2px solid #3182CE';
                                  setSelectedImage(e.target);
                                  const w = e.target.style.width || '';
                                  setImageWidth(w);
                                } else {
                                  doc.querySelectorAll('img').forEach(img => { img.style.outline = ''; });
                                  setSelectedImage(null);
                                  setImageWidth('');
                                }
                              });
                              doc.addEventListener('dragover', (e) => {
                                e.preventDefault();
                              });
                              doc.addEventListener('drop', (e) => {
                                e.preventDefault();
                                insertImageFiles(e.dataTransfer?.files);
                              });
                              doc.addEventListener('paste', (e) => {
                                e.preventDefault();
                                const imageFiles = Array.from(e.clipboardData?.files || []).filter(file => file.type?.startsWith('image/'));
                                if (imageFiles.length > 0) {
                                  insertImageFiles(imageFiles);
                                  return;
                                }
                                const text = e.clipboardData.getData('text/plain');
                                if (isLikelyImageUrl(text)) {
                                  doc.execCommand('insertHTML', false, `<p><img src="${escapeAttr(text.trim())}" alt="" style="display:block;max-width:100%;height:auto;border-radius:8px;margin:16px 0;" /></p>`);
                                  return;
                                }
                                doc.execCommand('insertText', false, text);
                              });
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Subject</Text>
                      <Text fontSize="sm" fontWeight="bold">{selectedEmail.subject}</Text>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="medium" color="gray.500" mb={1}>Preview</Text>
                      <Box
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        p={0}
                        flex="1"
                        overflow="auto"
                      >
                        <iframe
                          title="Email Preview"
                          srcDoc={selectedEmail.body}
                          style={{ width: '100%', minHeight: '60vh', border: 'none' }}
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </VStack>
            )}
          </DrawerBody>
          <DrawerFooter borderTop="1px solid" borderColor="gray.200">
            {selectedEmail && (
              <HStack spacing={2} w="100%" justify="space-between">
                <HStack spacing={2}>
                  {selectedEmail.status === 'compose' && (
                    <Button size="sm" onClick={onDetailClose}>Close</Button>
                  )}
                  {selectedEmail.status === 'draft' && (
                    <>
                      {isEditing ? (
                        <>
                          <Button size="sm" onClick={() => setIsEditing(false)}>Cancel</Button>
                          <Button size="sm" colorScheme="blue" onClick={saveEdit}>Save</Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="outline" leftIcon={<EditIcon />} onClick={() => { setEditSubject(selectedEmail.subject); setEditBody(selectedEmail.body); setIsEditing(true); }}>
                            Edit
                          </Button>
                          {selectedEmail.email_type !== 'newsletter' && (
                            <Button size="sm" variant="outline" leftIcon={<RepeatIcon />} onClick={() => regenerateEmail(selectedEmail.id)} isLoading={generating}>
                              Regenerate
                            </Button>
                          )}
                        </>
                      )}
                    </>
                  )}
                </HStack>
                <HStack spacing={2}>
                  {selectedEmail.status === 'compose' && (
                    <Button
                      size="sm"
                      colorScheme="blue"
                      onClick={saveNewsletterDraft}
                      isLoading={sendingId === 'newsletter-compose'}
                    >
                      Save Draft
                    </Button>
                  )}
                  {selectedEmail.status === 'draft' && !isEditing && (
                    <>
                      <Button size="sm" colorScheme="red" variant="ghost" onClick={() => deleteEmail(selectedEmail.id)}>Delete</Button>
                      <Button size="sm" colorScheme="blue" onClick={() => approveEmail(selectedEmail.id)}>Approve</Button>
                    </>
                  )}
                  {selectedEmail.status === 'approved' && (
                    <>
                      <Button size="sm" colorScheme="red" variant="ghost" onClick={() => deleteEmail(selectedEmail.id)}>Delete</Button>
                      <Button size="sm" colorScheme="green" leftIcon={<Send size={14} />} onClick={() => sendEmail(selectedEmail.id)} isLoading={sendingId === selectedEmail.id}>
                        {selectedEmail.email_type === 'newsletter' ? 'Send Newsletter' : 'Send Email'}
                      </Button>
                    </>
                  )}
                </HStack>
              </HStack>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default AdminPilotOutreachTab;
